import { lazy } from "react";
import { Main } from "../pages/Main";
import { AboutMe } from "../pages/AboutMe";
import Resources from "../pages/Resources/Resources";
import PhotoGallery from "../pages/PhotoGallery/PhotoGallery";
import Prices from "../pages/Prices/Prices";
import Contacts from "../pages/Contacts/Contacts";
import Basket from "../pages/Basket/Basket";
import NotFound from "../pages/NotFound/NotFound";
import { Login } from "../components/Login";
import { IRoute } from "../types";
import { FormOrder } from "../pages/Basket/components/FormOrder/FormOrder";
import { SendOnEmailActivateLink } from "../components/SendOnEmailActivateLink/SendOnEmailActivateLink";
import { PasswordChange } from "../components/PasswordChange/PasswordChange";
import { Blog } from "../pages/Blog";
import { BlogArticleDetails } from "../pages/Blog/components/BlogArticleDetails";
import OrderSuccess from "../pages/OrderSuccess/OrderSuccess";
import { BabyHub, FamilyHub, MaternityHub, NewbornHub } from "../pages/Resources/components/newborn-pages";
import { ResourceArticlePage } from "../pages/Resources/components/newborn-pages/ResourceArticlePage";
import {
  BabyDoesNotSleepArticleRedirect,
  ChristeningPricingRedirect,
  FamilySessionRedirect,
  NewbornAnxietyArticleRedirect,
  NewbornSessionRedirect,
  SessionInformationRedirect,
} from "./LegacyRedirects";

const Admin = lazy(() => import("../pages/Admin").then((module) => ({ default: module.Admin })));
const Profile = lazy(() => import("../pages/Profile").then((module) => ({ default: module.Profile })));
const EditorPage = lazy(() =>
  import("../components/EditorPage").then((module) => ({ default: module.EditorPage }))
);
const AddArticleInBlog = lazy(() =>
  import("../pages/Admin/components/AddArticleInBlog").then((module) => ({ default: module.AddArticleInBlog }))
);
const AddNewPacket = lazy(() =>
  import("../pages/Admin/components/AddNewPacket").then((module) => ({ default: module.AddNewPacket }))
);
const AddNewPhoto = lazy(() =>
  import("../pages/Admin/components/AddNewPhoto").then((module) => ({ default: module.AddNewPhoto }))
);
const OrderList = lazy(() =>
  import("../pages/Admin/components/OrderList").then((module) => ({ default: module.OrderList }))
);
const ShareExperience = lazy(() => import("../pages/ShareExperience/ShareExperience"));
const ReviewAdmin = lazy(() => import("../pages/Admin/components/ReviewAdmin/ReviewAdmin"));

export const routes: IRoute[] = [
  { component: Main, path: "", name: "main", isAdmin: false, isAuth: false, index: true },
  { component: AboutMe, path: "about", name: "about", isAdmin: false, isAuth: false },
  { component: Resources, path: "resources", name: "resources", isAdmin: false, isAuth: false },
  { component: NewbornHub, path: "resources/newborn", name: "newbornHub", isAdmin: false, isAuth: false },
  { component: MaternityHub, path: "resources/maternity", name: "maternityHub", isAdmin: false, isAuth: false },
  { component: BabyHub, path: "resources/baby", name: "babyHub", isAdmin: false, isAuth: false },
  { component: FamilyHub, path: "resources/family", name: "familyHub", isAdmin: false, isAuth: false },
  {
    component: ResourceArticlePage,
    path: "resources/:category/:slug",
    name: "resourceArticle",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: PhotoGallery,
    path: "gallery",
    subPath: [
      "gallery/newborn",
      "gallery/maternity",
      "gallery/baby",
      "gallery/family",
      "gallery/woman",
      "gallery/discharge",
      "gallery/christening",
    ],
    name: "gallery",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: Prices,
    path: "prices",
    subPath: [
      "prices/newborn",
      "prices/baby",
      "prices/family",
      "prices/maternity",
    ],
    name: "prices",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: ChristeningPricingRedirect,
    path: "prices/christening",
    name: "christening-pricing-redirect",
    isAdmin: false,
    isAuth: false,
  },
  { component: Contacts, path: "contact", name: "contact", isAdmin: false, isAuth: false },
  { component: ShareExperience, path: "share-your-experience/:token", name: "shareExperience", isAdmin: false, isAuth: false },
  { component: Basket, path: "basket", name: "basket", isAdmin: false, isAuth: false, protectRouteBasket: true },
  {
    component: OrderSuccess,
    path: "order-success",
    name: "order-success",
    isAdmin: false,
    isAuth: false,
    protectOrderSuccess: true,
  },
  { component: Login, path: "signin", name: "signin", isAdmin: false, isAuth: false, withOutHeaderAndFooter: true },
  {
    component: SendOnEmailActivateLink,
    path: "forgot",
    name: "forgot",
    isAdmin: false,
    isAuth: false,
    withOutHeaderAndFooter: true,
  },
  {
    component: PasswordChange,
    path: "passwordChange/:id",
    name: "passwordChange",
    isAdmin: false,
    isAuth: false,
    withOutHeaderAndFooter: true,
  },
  {
    component: SessionInformationRedirect,
    path: "session-information",
    name: "session-information",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: NewbornSessionRedirect,
    path: "newborn-session",
    name: "newborn-session",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: FamilySessionRedirect,
    path: "family-session",
    name: "family-session",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: NewbornAnxietyArticleRedirect,
    path: "blog/how-to-avoid-anxiety-and-worries-during-a-newborn-photoshoot",
    name: "newborn-anxiety-article-redirect",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: BabyDoesNotSleepArticleRedirect,
    path: "blog/what-to-do-if-the-baby-does-not-sleep",
    name: "baby-does-not-sleep-article-redirect",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: BlogArticleDetails,
    path: "blog/:url",
    name: "articleDetail",
    isAdmin: false,
    isAuth: false,
  },
  {
    component: Blog,
    path: "blog",
    name: "blog",
    isAdmin: false,
    isAuth: false,
  },

  {
    component: FormOrder,
    path: "checkout",
    name: "checkout",
    isAdmin: false,
    isAuth: false,
    protectRouteBasket: true,
  },
  { component: NotFound, path: "not-found", name: "not-found", isAdmin: false, isAuth: false },
];

export const ProtectRoutes = [
  { component: Admin, path: "admin", name: "adminName", isAdmin: true, isAuth: false },
  { component: Profile, path: "profile", name: "profile", isAdmin: true, isAuth: false },
  { component: EditorPage, path: "editor", name: "editor", isAdmin: true, isAuth: false },
];

export const subRoutes = [
  {
    component: AddArticleInBlog,
    path: "admin/article-in-blog",
    name: "AddArticleInBlog",
    isAdmin: true,
    isAuth: false,
  },
  { component: AddNewPacket, path: "admin/addNewPacket", name: "addNewPacket", isAdmin: true, isAuth: false },
  { component: AddNewPhoto, path: "admin/addNewPhoto", name: "addNewPhoto", isAdmin: true, isAuth: false },
  { component: OrderList, path: "admin/orderList", name: "orderList", isAdmin: true, isAuth: false },
  { component: ReviewAdmin, path: "admin/reviews", name: "reviews", isAdmin: true, isAuth: false },
];

export const allRoutes = [...routes, ...ProtectRoutes, ...subRoutes];
